/**
 * Google Tag Manager utility for conditional loading
 * Follows the same enable/disable logic as Sentry
 */

// Check if GTM should be enabled (same logic as Sentry)
const isProduction = import.meta.env.MODE === 'production';
const gtmEnabled = import.meta.env.VITE_GTM_ENABLED === 'true' || isProduction;
const gtmId = import.meta.env.VITE_GTM_ID || 'GTM-NBVP5K72';

export const initializeGTM = () => {
  if (!gtmEnabled) {
    return;
  }

  if (!gtmId) {
    console.warn('[GTM] No GTM ID provided. Skipping initialization.');
    return;
  }

  // Add GTM script to head
  const script = document.createElement('script');
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
  document.head.appendChild(script);

  // Add GTM noscript iframe to body
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
};

// Push events to dataLayer (safe even when GTM is disabled)
export const pushToDataLayer = (data: Record<string, any>) => {
  if (!gtmEnabled) {
    console.debug('[GTM] Event not sent (disabled):', data);
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
};

// Helper function to track page views
export const trackPageView = (path: string, title?: string) => {
  pushToDataLayer({
    event: 'page_view',
    page_path: path,
    page_title: title || document.title,
  });
};

// Helper function to track custom events
export const trackEvent = (eventName: string, eventData?: Record<string, any>) => {
  pushToDataLayer({
    event: eventName,
    ...eventData,
  });
};

// Export configuration for debugging
export const gtmConfig = {
  enabled: gtmEnabled,
  id: gtmId,
  environment: import.meta.env.MODE,
};

// Add TypeScript declaration for window.dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}